import type { AuthAccount } from './auth.api';

const AUTH_ACCESS_TOKEN_COOKIE_KEY = 'matcha-access-token';
const LEGACY_AUTH_SESSION_STORAGE_KEY = 'admin-auth-session';
const REMEMBERED_AUTH_COOKIE_MAX_AGE_SECONDS = 60 * 60 * 24 * 30;

function isIpv4Hostname(hostname: string): boolean {
  const octets = hostname.split('.');
  if (octets.length !== 4) {
    return false;
  }

  return octets.every((octet) => {
    if (!/^\d+$/.test(octet)) {
      return false;
    }

    const value = Number(octet);
    return Number.isInteger(value) && value >= 0 && value <= 255;
  });
}

function inferCookieDomainFromHostname(hostname: string): string | null {
  const normalizedHostname = hostname.trim().toLowerCase();
  if (
    !normalizedHostname ||
    normalizedHostname === 'localhost' ||
    normalizedHostname.endsWith('.localhost') ||
    normalizedHostname.includes(':') ||
    isIpv4Hostname(normalizedHostname)
  ) {
    return null;
  }

  const labels = normalizedHostname.split('.').filter(Boolean);
  if (labels.length < 2) {
    return null;
  }

  if (labels.length === 2) {
    return labels.join('.');
  }

  const topLevelLabel = labels[labels.length - 1];
  const secondLevelLabel = labels[labels.length - 2];
  const isCountryCodeSuffix = topLevelLabel.length === 2 && secondLevelLabel.length <= 3;

  return isCountryCodeSuffix ? labels.slice(-3).join('.') : labels.slice(-2).join('.');
}

function normalizeCookieDomain(value: string): string | null {
  const normalizedValue = value.trim().toLowerCase();
  if (!normalizedValue) {
    return null;
  }

  const withoutScheme = normalizedValue.replace(/^[a-z][a-z\d+\-.]*:\/\//i, '');
  const hostWithOptionalPort = withoutScheme.split('/')[0]?.trim() ?? '';
  if (!hostWithOptionalPort) {
    return null;
  }

  const host = hostWithOptionalPort
    .replace(/^\.+/, '')
    .replace(/\.+$/, '')
    .split(':')[0]
    ?.trim();
  if (!host) {
    return null;
  }

  if (
    host === 'localhost' ||
    host.endsWith('.localhost') ||
    host.includes(':') ||
    isIpv4Hostname(host)
  ) {
    return null;
  }

  const labels = host.split('.').filter(Boolean);
  if (labels.length < 2) {
    return null;
  }

  const hasInvalidLabel = labels.some(
    (label) =>
      label.length > 63 ||
      label.startsWith('-') ||
      label.endsWith('-') ||
      !/^[a-z0-9-]+$/i.test(label)
  );

  if (hasInvalidLabel) {
    return null;
  }

  return labels.join('.');
}

function isHostnameWithinDomain(hostname: string, domain: string): boolean {
  const normalizedHostname = hostname.trim().toLowerCase();
  const normalizedDomain = domain.trim().toLowerCase().replace(/^\.+/, '');

  return (
    Boolean(normalizedHostname) &&
    Boolean(normalizedDomain) &&
    (normalizedHostname === normalizedDomain ||
      normalizedHostname.endsWith(`.${normalizedDomain}`))
  );
}

function resolveCookieDomain(): string | null {
  const configuredCookieDomain = normalizeCookieDomain(
    process.env.REACT_APP_AUTH_COOKIE_DOMAIN ?? ''
  );

  if (typeof window === 'undefined') {
    return configuredCookieDomain;
  }

  const inferredCookieDomain = inferCookieDomainFromHostname(window.location.hostname);
  if (!configuredCookieDomain) {
    return inferredCookieDomain;
  }

  // Browsers reject Set-Cookie when Domain does not match the current host.
  if (isHostnameWithinDomain(window.location.hostname, configuredCookieDomain)) {
    return configuredCookieDomain;
  }

  return inferredCookieDomain;
}

interface LegacyAuthSession {
  accessToken?: unknown;
  account?: {
    uuid?: unknown;
    username?: unknown;
  };
}

interface LegacySessionResult {
  accessToken: string;
  account: AuthAccount;
  persistent: boolean;
}

function isAuthAccount(value: unknown): value is AuthAccount {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const typedValue = value as Partial<AuthAccount>;
  return (
    typeof typedValue.uuid === 'string' &&
    typedValue.uuid.trim().length > 0 &&
    typeof typedValue.username === 'string' &&
    typedValue.username.trim().length > 0
  );
}

function parseLegacySession(rawValue: string | null): LegacyAuthSession | null {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as unknown;
    return parsed && typeof parsed === 'object' ? (parsed as LegacyAuthSession) : null;
  } catch {
    return null;
  }
}

function normalizeAccessToken(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readCookieValue(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const encodedName = encodeURIComponent(name);
  const cookieItems = document.cookie.split(';');

  for (const cookieItem of cookieItems) {
    const trimmedItem = cookieItem.trim();
    if (!trimmedItem.startsWith(`${encodedName}=`)) {
      continue;
    }

    const encodedValue = trimmedItem.slice(encodedName.length + 1);
    try {
      return decodeURIComponent(encodedValue);
    } catch {
      return encodedValue;
    }
  }

  return null;
}

function buildCookieAttributes(maxAgeSeconds?: number): string[] {
  const attributes = ['Path=/', 'SameSite=Lax'];
  const cookieDomain = resolveCookieDomain();

  if (cookieDomain) {
    attributes.push(`Domain=${cookieDomain}`);
  }

  if (typeof window !== 'undefined' && window.location.protocol === 'https:') {
    attributes.push('Secure');
  }

  if (typeof maxAgeSeconds === 'number' && Number.isFinite(maxAgeSeconds)) {
    attributes.push(`Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}`);
  }

  return attributes;
}

function writeCookieValue(name: string, value: string, maxAgeSeconds?: number): void {
  if (typeof document === 'undefined') {
    return;
  }

  const encodedCookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; ${buildCookieAttributes(
    maxAgeSeconds
  ).join('; ')}`;
  document.cookie = encodedCookie;
}

function decodeBase64Url(value: string): string | null {
  const normalizedValue = value.replace(/-/g, '+').replace(/_/g, '/');
  const paddingLength = (4 - (normalizedValue.length % 4)) % 4;
  const paddedValue = `${normalizedValue}${'='.repeat(paddingLength)}`;

  try {
    const binaryValue = atob(paddedValue);
    const bytes = Uint8Array.from(binaryValue, (char) => char.charCodeAt(0));
    return new TextDecoder().decode(bytes);
  } catch {
    return null;
  }
}

function readLegacySession(): LegacySessionResult | null {
  if (typeof window === 'undefined') {
    return null;
  }

  const localValue = parseLegacySession(
    window.localStorage.getItem(LEGACY_AUTH_SESSION_STORAGE_KEY)
  );
  const localAccessToken = normalizeAccessToken(localValue?.accessToken);

  if (localAccessToken && isAuthAccount(localValue?.account)) {
    return {
      accessToken: localAccessToken,
      account: {
        uuid: localValue.account.uuid.trim(),
        username: localValue.account.username.trim(),
      },
      persistent: true,
    };
  }

  const sessionValue = parseLegacySession(
    window.sessionStorage.getItem(LEGACY_AUTH_SESSION_STORAGE_KEY)
  );
  const sessionAccessToken = normalizeAccessToken(sessionValue?.accessToken);

  if (sessionAccessToken && isAuthAccount(sessionValue?.account)) {
    return {
      accessToken: sessionAccessToken,
      account: {
        uuid: sessionValue.account.uuid.trim(),
        username: sessionValue.account.username.trim(),
      },
      persistent: false,
    };
  }

  return null;
}

export function readAccessTokenFromCookie(): string | null {
  const cookieValue = readCookieValue(AUTH_ACCESS_TOKEN_COOKIE_KEY);
  const normalizedValue = normalizeAccessToken(cookieValue);
  return normalizedValue || null;
}

export function persistAccessTokenToCookie(
  accessToken: string,
  options?: {
    persistent?: boolean;
  }
): void {
  const normalizedToken = normalizeAccessToken(accessToken);
  if (!normalizedToken) {
    return;
  }

  writeCookieValue(
    AUTH_ACCESS_TOKEN_COOKIE_KEY,
    normalizedToken,
    options?.persistent ? REMEMBERED_AUTH_COOKIE_MAX_AGE_SECONDS : undefined
  );
}

export function clearAccessTokenCookie(): void {
  writeCookieValue(AUTH_ACCESS_TOKEN_COOKIE_KEY, '', 0);
}

export function parseAccountFromAccessToken(accessToken: string): AuthAccount | null {
  const normalizedToken = normalizeAccessToken(accessToken);
  if (!normalizedToken) {
    return null;
  }

  const tokenSegments = normalizedToken.split('.').filter(Boolean);
  const payloadCandidates: string[] = [];

  if (tokenSegments.length >= 3) {
    // JWT layout: header.payload.signature
    payloadCandidates.push(tokenSegments[1]);
  }

  if (tokenSegments.length >= 1) {
    // Legacy Matcha Events token layout: payload.signature
    payloadCandidates.push(tokenSegments[0]);
  }

  for (const payloadSegment of payloadCandidates) {
    const decodedPayload = decodeBase64Url(payloadSegment);
    if (!decodedPayload) {
      continue;
    }

    try {
      const payload = JSON.parse(decodedPayload) as {
        sub?: unknown;
        uuid?: unknown;
        username?: unknown;
        preferred_username?: unknown;
      };

      const uuid = normalizeAccessToken(payload.uuid ?? payload.sub);
      const username = normalizeAccessToken(
        payload.username ?? payload.preferred_username
      );

      if (!uuid || !username) {
        continue;
      }

      return {
        uuid,
        username,
      };
    } catch {
      continue;
    }
  }

  return null;
}

export function clearLegacyAuthSessionStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  window.localStorage.removeItem(LEGACY_AUTH_SESSION_STORAGE_KEY);
  window.sessionStorage.removeItem(LEGACY_AUTH_SESSION_STORAGE_KEY);
}

export function migrateLegacyAuthSessionToCookie():
  | {
      accessToken: string;
      account: AuthAccount;
    }
  | null {
  const legacySession = readLegacySession();
  if (!legacySession) {
    return null;
  }

  persistAccessTokenToCookie(legacySession.accessToken, {
    persistent: legacySession.persistent,
  });
  clearLegacyAuthSessionStorage();

  return {
    accessToken: legacySession.accessToken,
    account: legacySession.account,
  };
}
